import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAllTemplatesThunk } from "../../../../../store/thunks/jewelleryPlan/planTemplateThunks";
import {
  PLAN_TYPES,
  BENEFIT_TYPES,
} from "../../../../../constants/jewellery.constant";
import {
  SparklesIcon,
  ScaleIcon,
  LockClosedIcon,
} from "@heroicons/react/outline";

const TemplateSelection = ({ data, onNext, onBack }) => {
  const dispatch = useDispatch();
  const { templates = [], loading } = useSelector(
    (state) => state.jewelleryPlan?.planTemplates || {},
  );
  const [selectedTemplate, setSelectedTemplate] = useState(data.planTemplates);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    dispatch(getAllTemplatesThunk({ isActive: true }));
  }, [dispatch]);

  const getTemplateIcon = (type) => {
    switch (type) {
      case PLAN_TYPES.VALUE_SAVINGS:
        return SparklesIcon;
      case PLAN_TYPES.WEIGHT_SAVINGS:
        return ScaleIcon;
      case PLAN_TYPES.PRICE_LOCK:
        return LockClosedIcon;
      default:
        return SparklesIcon;
    }
  };

  const getBenefitDescription = (template) => {
    switch (template.benefitType) {
      case BENEFIT_TYPES.FREE_INSTALLMENT:
        return `Get ${
          template.benefitParams?.freeInstallments || 1
        } free installment after ${
          template.benefitParams?.triggerAfterN || 10
        } payments`;
      case BENEFIT_TYPES.VA_DISCOUNT_CAP:
        return `VA capped at ${template.benefitParams?.maxPercent || 0}%`;
      case BENEFIT_TYPES.MAKING_CHARGE_WAIVER:
        return `${
          template.benefitParams?.waiveChargesPercent || 0
        }% off on making charges`;
      default:
        return "Standard benefits apply";
    }
  };

  const filteredTemplates = templates.filter(
    (template) => filter === "all" || template.type === filter,
  );

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
  };

  const handleProceed = () => {
    if (selectedTemplate) {
      onNext({
        templateId: selectedTemplate._id,
        template: selectedTemplate,
      });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-medium text-text mb-4">
          Select Plan Template
        </h2>

        {/* Filter Tabs */}
        <div className="border-b border-neutral-light mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setFilter("all")}
              className={`py-2 px-1 border-b-2 font-medium text-sm
                ${
                  filter === "all"
                    ? "border-primary text-primary"
                    : "border-transparent text-neutral-dark hover:text-text hover:border-neutral-light"
                }`}
            >
              All Templates
            </button>
            <button
              onClick={() => setFilter(PLAN_TYPES.VALUE_SAVINGS)}
              className={`py-2 px-1 border-b-2 font-medium text-sm
                ${
                  filter === PLAN_TYPES.VALUE_SAVINGS
                    ? "border-primary text-primary"
                    : "border-transparent text-neutral-dark hover:text-text hover:border-neutral-light"
                }`}
            >
              Value Savings
            </button>
            <button
              onClick={() => setFilter(PLAN_TYPES.WEIGHT_SAVINGS)}
              className={`py-2 px-1 border-b-2 font-medium text-sm
                ${
                  filter === PLAN_TYPES.WEIGHT_SAVINGS
                    ? "border-primary text-primary"
                    : "border-transparent text-neutral-dark hover:text-text hover:border-neutral-light"
                }`}
            >
              Weight Savings
            </button>
            <button
              onClick={() => setFilter(PLAN_TYPES.PRICE_LOCK)}
              className={`py-2 px-1 border-b-2 font-medium text-sm
                ${
                  filter === PLAN_TYPES.PRICE_LOCK
                    ? "border-primary text-primary"
                    : "border-transparent text-neutral-dark hover:text-text hover:border-neutral-light"
                }`}
            >
              Price Lock
            </button>
          </nav>
        </div>

        {/* Template Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-neutral-light rounded-lg h-48"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTemplates.map((template) => {
              const Icon = getTemplateIcon(template.type);
              const isSelected = selectedTemplate?._id === template._id;

              return (
                <div
                  key={template._id}
                  onClick={() => handleSelectTemplate(template)}
                  className={`relative rounded-lg border-2 p-4 cursor-pointer transition-all
                    ${
                      isSelected
                        ? "border-primary bg-primary-light"
                        : "border-neutral-light hover:border-neutral-dark bg-background"
                    }`}
                >
                  {isSelected && (
                    <div className="absolute top-2 right-2">
                      <div className="bg-primary text-text-on-primary rounded-full p-1">
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center mb-3">
                    <div className="flex-shrink-0">
                      <Icon
                        className={`h-8 w-8 ${isSelected ? "text-white" : "text-primary"}`}
                      />
                    </div>
                    <div className="ml-3">
                      <h3
                        className={`text-sm font-medium ${isSelected ? "text-white" : "text-text"}`}
                      >
                        {template.name}
                      </h3>
                      <p
                        className={`text-xs ${isSelected ? "text-gray-200" : "text-neutral-dark"}`}
                      >
                        {template.code}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between">
                      <span
                        className={
                          isSelected ? "text-gray-200" : "text-neutral-dark"
                        }
                      >
                        Tenure:
                      </span>
                      <span
                        className={`font-medium ${isSelected ? "text-white" : "text-text"}`}
                      >
                        {template.tenureMonths} months
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span
                        className={
                          isSelected ? "text-gray-200" : "text-neutral-dark"
                        }
                      >
                        Min Amount:
                      </span>
                      <span
                        className={`font-medium ${isSelected ? "text-white" : "text-text"}`}
                      >
                        ₹{template.minInstallment}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span
                        className={
                          isSelected ? "text-gray-200" : "text-neutral-dark"
                        }
                      >
                        Max Amount:
                      </span>
                      <span
                        className={`font-medium ${isSelected ? "text-white" : "text-text"}`}
                      >
                        ₹{template.maxInstallment}
                      </span>
                    </div>
                  </div>

                  <div
                    className={`mt-3 pt-3 border-t ${isSelected ? "border-gray-600" : "border-neutral-light"}`}
                  >
                    <p
                      className={`text-xs ${isSelected ? "text-white" : "text-text"}`}
                    >
                      {getBenefitDescription(template)}
                    </p>
                  </div>

                  {template.description && (
                    <p
                      className={`mt-2 text-xs ${isSelected ? "text-gray-200" : "text-neutral-dark"}`}
                    >
                      {template.description}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Selected Template Details */}
      {selectedTemplate && (
        <div className="bg-secondary-light border border-secondary rounded-lg p-4">
          <h3 className="text-sm font-medium text-secondary-dark mb-2">
            Selected: {selectedTemplate.name}
          </h3>
          <div className="text-xs text-secondary-dark space-y-1">
            <p>• Tenure: {selectedTemplate.tenureMonths} months</p>
            <p>
              • Amount Range: ₹{selectedTemplate.minInstallment} - ₹
              {selectedTemplate.maxInstallment}
            </p>
            <p>• {getBenefitDescription(selectedTemplate)}</p>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-4 py-2 border border-neutral-light rounded-md text-sm font-medium text-text bg-background hover:bg-neutral-light"
        >
          Back
        </button>
        <button
          onClick={handleProceed}
          disabled={!selectedTemplate}
          className={`px-6 py-2 rounded-md text-sm font-medium
            ${
              selectedTemplate
                ? "bg-primary text-text-on-primary hover:bg-primary-dark"
                : "bg-neutral-light text-neutral-dark cursor-not-allowed"
            }`}
        >
          Continue
        </button>
      </div>
    </div>
  );
};

export default TemplateSelection;
